import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigationType} from 'react-router';

import NAVIGATION_TYPES from '@/consts/navigationTypes';
import useScrollMessages from '@/hooks/useScrollMessages';

type SlideTransitionProps = {
    children: ReactElement;
};

const DURATION_MS = 250;

/**
 * Animates route transitions with a horizontal slide.
 * - PUSH/REPLACE: old page exits left, new page enters from right.
 * - POP (back): old page exits right, new page enters from left.
 *
 * Implementation: two-pane track shifted via translate3d, same approach as
 * the main app's TabTransitionHost.
 */
const SlideTransition = ({children}: SlideTransitionProps) => {
    const activePaneRef = useRef<HTMLDivElement>(null);
    useScrollMessages(activePaneRef);
    const location = useLocation();
    const navigationType = useNavigationType();

    const [displayedChildren, setDisplayedChildren] = useState(children);
    const [prevChildren, setPrevChildren] = useState<ReactElement | null>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [transitionEnabled, setTransitionEnabled] = useState(false);
    const [transform, setTransform] = useState('translate3d(0, 0, 0)');
    const [direction, setDirection] = useState<'forward' | 'back'>('forward');

    const prevKeyRef = useRef(location.key);
    const prevPathnameRef = useRef(location.pathname);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const completeTransition = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setPrevChildren(null);
        setIsAnimating(false);
        setTransitionEnabled(false);
        setTransform('translate3d(0, 0, 0)');
    }, []);

    useEffect(() => {
        if (location.key === prevKeyRef.current) {
            // Same location — update content in place
            setDisplayedChildren(children);
            return;
        }

        // REPLACE navigations are silent — no animation (e.g. initial redirect)
        if (navigationType === NAVIGATION_TYPES.REPLACE) {
            setDisplayedChildren(children);
            prevKeyRef.current = location.key;
            prevPathnameRef.current = location.pathname;
            return;
        }

        // Same pathname (only search/hash changed, e.g. popup open/close) — no animation
        if (location.pathname === prevPathnameRef.current) {
            setDisplayedChildren(children);
            prevKeyRef.current = location.key;
            return;
        }

        const isBack = navigationType === NAVIGATION_TYPES.POP;
        setDirection(isBack ? 'back' : 'forward');

        // Save current as "previous" before updating
        setPrevChildren(displayedChildren);
        setDisplayedChildren(children);
        prevKeyRef.current = location.key;
        prevPathnameRef.current = location.pathname;

        setIsAnimating(true);
        setTransitionEnabled(false);
        // Initial position: show old pane
        setTransform(isBack ? 'translate3d(-50%, 0, 0)' : 'translate3d(0, 0, 0)');

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTransitionEnabled(true);
                // Slide to new pane
                setTransform(isBack ? 'translate3d(0, 0, 0)' : 'translate3d(-50%, 0, 0)');
            });
        });

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(completeTransition, DURATION_MS + 50);
    }, [location.key, navigationType, children, completeTransition, displayedChildren]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        if (e.propertyName !== 'transform') return;
        completeTransition();
    };

    const trackStyle: React.CSSProperties = {
        width: isAnimating ? '200%' : '100%',
        transform,
        transitionProperty: transitionEnabled ? 'transform' : 'none',
        transitionDuration: `${DURATION_MS}ms`,
        transitionTimingFunction: 'ease-in-out',
    };

    // Build panes order based on direction
    const panes =
        isAnimating && prevChildren
            ? direction === 'forward'
                ? [prevChildren, displayedChildren]
                : [displayedChildren, prevChildren]
            : [displayedChildren];

    return (
        <div className='relative h-full w-full overflow-hidden'>
            <div className='flex h-full' style={trackStyle} onTransitionEnd={handleTransitionEnd}>
                {panes.map((pane, i) => (
                    <div
                        key={`pane-${i}`}
                        ref={i === panes.length - 1 ? activePaneRef : undefined}
                        className='h-full shrink-0 overflow-auto'
                        style={{width: isAnimating ? '50%' : '100%'}}
                    >
                        {pane}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SlideTransition;
